import HelpOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Stack, Tooltip } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFetcher, useLocation, useNavigation } from '@remix-run/react';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { checkValidImageUrl } from '~/utils/common';

export default function UploadAutomockupLogo() {
  const fetcher = useFetcher();
  const submit = fetcher.submit;
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';
  const location = useLocation();

  const [errors, setErrors] = React.useState<{ [key: string]: string | null }>({
    dropzone: null,
    importFromUrl: null,
  });

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      const formData = new FormData();
      acceptedFiles.forEach((file) => {
        formData.append('img', file, file.name);
      });

      formData.append('pathname', location.pathname);

      submit(formData, {
        action: '/api/artworks',
        method: 'post',
        encType: 'multipart/form-data',
        replace: true,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [submit]
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': [], 'application/pdf': [], 'application/postscript': [] },
  });

  const handleSubmitFromUrl = async () => {
    const url = document.querySelector<HTMLInputElement>('#logoFromUrl')?.value || '';

    const isValid = await checkValidImageUrl(url);

    if (isValid) {
      submit(
        { _action: 'upload-logo-from-url', logoUrl: url },
        { action: '/api/artworks', method: 'post', replace: true }
      );
    } else {
      setErrors({ ...errors, importFromUrl: 'Please insert a valid url' });
    }
  };

  return (
    <Stack>
      <Typography variant="body1" my={1.5}>
        Sorry, we couldn’t detect your company. Would you like to view our catalog with your company’s logo?
      </Typography>
      <Stack
        {...getRootProps()}
        alignItems="center"
        p={2}
        bgcolor="#FFF"
        border="2px dashed #efefef"
        borderRadius={1}
        zIndex={1304}
        sx={{
          opacity: isSubmitting ? 0.7 : 1,
          cursor: 'pointer',
        }}
      >
        <input disabled={isSubmitting} name="img" {...getInputProps()} />
        <Stack flexDirection="row" justifyContent="center" alignItems="center">
          <Avatar sx={{ bgcolor: '#E5EEF9' }}>
            <UploadFileOutlinedIcon sx={{ color: 'primary.main' }} />
          </Avatar>
          <Typography
            ml={1}
            component="span"
            variant="body1"
            sx={{ color: 'primary.main', textDecoration: 'underline' }}
          >
            Click to upload
          </Typography>
        </Stack>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#000000' }}>
          or drag and drop your artwork
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          SVG, PNG, JPG, Ai, PDF or EPS (max. 3MB)
        </Typography>
      </Stack>
      <Divider sx={{ my: 2, '&::before , &::after': { top: 'auto' } }}>
        <Typography fontWeight={600}>Or</Typography>
      </Divider>
      <Stack>
        <Stack flexDirection="row" alignItems="center" mb={1}>
          <Typography mr={1} fontWeight={600}>
            Import logo from url
          </Typography>
          <Tooltip title="Insert a valid image URL to use as a logo" placement="top" arrow>
            <HelpOutlinedIcon color="action" fontSize="small" />
          </Tooltip>
        </Stack>

        <Stack position="relative">
          <TextField
            id="logoFromUrl"
            size="small"
            fullWidth
            placeholder="Add your URL"
            onChange={() => {
              if (errors.importFromUrl) setErrors({ ...errors, importFromUrl: null });
            }}
            sx={{ '& .MuiOutlinedInput-input': { width: '72%' } }}
          />
          <Button onClick={() => handleSubmitFromUrl()} sx={{ position: 'absolute', inset: '0px 0px 0px auto', px: 2 }}>
            UPLOAD
          </Button>
        </Stack>
        {errors.importFromUrl && <Typography color="red">{errors.importFromUrl}</Typography>}
      </Stack>
    </Stack>
  );
}
